import type { ReactNode } from 'react';

interface GoldBadgeProps {
  children: ReactNode;
  variant?: 'gold' | 'off-market' | 'ssot' | 'muted';
  className?: string;
}

/**
 * Luxury pill-shaped badge component with gradient variants.
 *
 * Features:
 * - Pill shape (rounded-full px-3 py-1)
 * - Gold gradient for default variant
 * - Gold border for off-market variant (border border-gold bg-transparent)
 * - Emerald for SSOT variant (bg-emerald text-white)
 * - Uppercase text, 12px font (text-xs uppercase tracking-wider)
 */
export function GoldBadge({
  children,
  variant = 'gold',
  className = '',
}: GoldBadgeProps) {
  const variantStyles = {
    gold: 'bg-gradient-to-r from-gold to-[#b8942f] text-[#1c1917] border-transparent',
    'off-market': 'bg-transparent border-gold border-2 text-gold',
    ssot: 'bg-emerald text-white border-transparent',
    muted: 'bg-transparent border-white/20 text-stone-500',
  };

  return (
    <span
      className={`
        inline-flex items-center justify-center
        rounded-full px-4 py-1.5
        text-xs font-semibold uppercase tracking-[0.1em]
        border
        ${variantStyles[variant]}
        ${className}
      `}
    >
      {children}
    </span>
  );
}
